import React, { useState } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { ManagedReportSecurity } from '../Shared/ManagedReport';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    SlimUserAdminModel,
    SlimTenantAdminModel,
    mapSlimUserAdminModel,
    mapSlimTenantAdminModel,
} from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import GetUserAdminModelsRequest from '../../Interfaces/Requests/Administration/GetUserAdminModelsRequest';
import GetTenantAdminModelsRequest from '../../Interfaces/Requests/Administration/GetTenantAdminModelsRequest';
import GetUserAdminModelsResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';
import GetTenantAdminModelsResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';

export type ManagedReportSecurityEditResult = {
    operation: ManagedAdminChildOperation;
    reportSecurity: ManagedReportSecurity;
};

type AUDReportSecurityFormValues = {
    rsUserRoleID: number | null;
    rsUserID: number;
    rsTenantID: number;
};

type AUDReportSecuritiesProps = {
    reportID: number;
    reportName: string;
    reportGroupName: string;
    reportSecurities: ManagedReportSecurity[];
    initialSelectedReportSecurityID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetReportSecurities: (
        result: ManagedReportSecurityEditResult
    ) => void;
    close: () => void;
};

const AUDReportSecurities: React.FC<AUDReportSecuritiesProps> = ({ close, ...props }) => {
    const { user } = useAuth();
    const [formAUDReportSecurity] = Form.useForm();

    const [rsUsersIncludeDisabled] = useState(false);
    const [rsTenantsIncludeDisabled] = useState(false);

    const reportSecurities = props.reportSecurities ?? [];

    const getInitialRSMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedReportSecurity = (): ManagedReportSecurity | undefined => {
        if (!props.initialSelectedReportSecurityID || props.initialSelectedReportSecurityID <= 0) {
            return undefined;
        }

        return reportSecurities.find(
            (reportSecurity) => reportSecurity.id === props.initialSelectedReportSecurityID
        );
    };

    const formatRSDisplayID = (id?: number | null): string => {
        return id && id > 0 ? String(id) : 'new';
    };

    const buildReportSecurityNameConstruct = (rs: ManagedReportSecurity | undefined) => {
        const tenantName =
            rs?.tenantId && rs.tenantId > 0
                ? (tenants.find((tenant) => tenant.id === rs.tenantId)?.name ?? '')
                : '';

        const selectedUser =
            rs?.userId && rs.userId > 0
                ? users.find((user) => user.id === rs.userId)
                : undefined;

        const userName =
            selectedUser
                ? `${selectedUser.firstName} ${selectedUser.lastName}`.trim()
                : '';

        return 'RID: ' + formatRSDisplayID(rs?.reportId) +
            ' / TID: [' + (rs?.tenantId ?? 0) + '] ' + tenantName +
            ' / UID: [' + (rs?.userId ?? 0) + '] ' + userName;
    };

    const initialSelectedReportSecurity = getInitialSelectedReportSecurity();
    const initialRSMode = getInitialRSMode();

    const initialRSFormValues = initialRSMode === 'add'
        ? {
            rsUserID: undefined,
            rsTenantID: undefined,
            rsUserRoleID: undefined,
        }
        : {
            rsUserID: initialSelectedReportSecurity?.userId ?? undefined,
            rsTenantID: initialSelectedReportSecurity?.tenantId ?? undefined,
            rsUserRoleID: initialSelectedReportSecurity?.userRoleId ?? undefined,
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialRSMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialRSMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialRSMode === 'delete');

    const [rsSelectedReportSecurityID, setRSSelectedReportSecurityID] = useState(initialSelectedReportSecurity?.id ?? -1);
    const [rsSelectedReportSecurityTitle, setRSSelectedReportSecurityTitle] = useState(
        initialSelectedReportSecurity ? buildReportSecurityNameConstruct(initialSelectedReportSecurity) : ''
    );

    const [rsUserID, setRSUserID] = useState<number | undefined>(initialSelectedReportSecurity?.userId ?? undefined);
    const [rsTenantID, setRSTenantID] = useState<number | undefined>(initialSelectedReportSecurity?.tenantId ?? undefined);
    const [rsUserRoleID, setRSUserRoleID] = useState<number | null>(initialSelectedReportSecurity?.userRoleId ?? null);

    const [rsSelectedReportSecurityCreatedBy, setRSSelectedReportSecurityCreatedBy] = useState(initialSelectedReportSecurity?.createdBy ?? -1);
    const [rsSelectedReportSecurityCreatedTs, setRSSelectedReportSecurityCreatedTs] = useState(initialSelectedReportSecurity?.createdTs ?? new Date());

    const rsPropsReportID = props.reportID > 0 ? props.reportID : 0;
    const rsPropsReportName = props.reportName !== '' ? props.reportName : 'New Report';
    const rsPropsReportGroupName = props.reportGroupName !== '' ? props.reportGroupName : 'New Report Group';


    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredRSUserOrTenant = (): Promise<void> => {
        const userID = formAUDReportSecurity.getFieldValue('rsUserID');
        const tenantID = formAUDReportSecurity.getFieldValue('rsTenantID');

        return isBlank(userID) && isBlank(tenantID) ? Promise.reject() : Promise.resolve();
    };

    const validateRequiredRSField = (fieldName: string): Promise<void> => {
        const value = formAUDReportSecurity.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateRSDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'rsUserID':
                return validateRequiredRSUserOrTenant();

            case 'rsTenantID':
                return validateRequiredRSField(rule.field);

            case 'rsUserRoleID':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const { data: users = [], isLoading: isGetUsersLoading } = useQuery<GetUserAdminModelsResponse[], Error, SlimUserAdminModel[]>(
        ['getUserAdminModelsRequest', rsUsersIncludeDisabled],
        async (): Promise<GetUserAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_MODELS}`;
                const requestData: GetUserAdminModelsRequest = { gua_includeDisabled: rsUsersIncludeDisabled };
                return await AxiosUtil.get<GetUserAdminModelsResponse[], GetUserAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleRSLoadError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleRSLoadError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error', err);
            },
            select: (response: GetUserAdminModelsResponse[]): SlimUserAdminModel[] => {
                return response
                    .map(mapSlimUserAdminModel)
                    .filter((eachUser) => !eachUser.deleted);
            },
        }
    );

    const { data: tenants = [], isLoading: isGetTenantsLoading } = useQuery<GetTenantAdminModelsResponse[], Error, SlimTenantAdminModel[]>(
        ['getTenantAdminModelsRequest', rsTenantsIncludeDisabled],
        async (): Promise<GetTenantAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_MODELS}`;
                const requestData: GetTenantAdminModelsRequest = { gta_includeDisabled: rsTenantsIncludeDisabled };
                return await AxiosUtil.get<GetTenantAdminModelsResponse[], GetTenantAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleRSLoadError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleRSLoadError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models] Internal Error', err);
            },
            select: (response: GetTenantAdminModelsResponse[]): SlimTenantAdminModel[] => {
                return response
                    .map(mapSlimTenantAdminModel)
                    .filter((eachTenant) => !eachTenant.deleted);
            },
        }
    );

    const handleRSUserIDChange = (value: number | undefined) => {
        setRSUserID(value);
    };

    const handleRSTenantIDChange = (value: number | undefined) => {
        setRSTenantID(value);
    };

    const handleRSUserRoleIDChange = (value: number | null | undefined) => {
        setRSUserRoleID(value ?? null);
    };

    const buildManagedReportSecurityRequest = (values: AUDReportSecurityFormValues): ManagedReportSecurity => {
        return {
            id: addButtonClicked ? 0 : (updButtonClicked ? rsSelectedReportSecurityID : (delButtonClicked ? rsSelectedReportSecurityID : 0)),
            reportId: rsPropsReportID,
            userRoleId: values.rsUserRoleID ?? null,
            userId: values.rsUserID,
            tenantId: values.rsTenantID,
            createdBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? rsSelectedReportSecurityCreatedBy : (delButtonClicked ? rsSelectedReportSecurityCreatedBy : 0)),
            modifiedBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? (user?.id ?? 0) : (delButtonClicked ? (user?.id ?? 0) : 0)),
            deletedBy: addButtonClicked ? 0 : (updButtonClicked ? 0 : (delButtonClicked ? (user?.id ?? 0) : 0)),
            createdTs: addButtonClicked ? (new Date()) : (updButtonClicked ? rsSelectedReportSecurityCreatedTs : (delButtonClicked ? rsSelectedReportSecurityCreatedTs : undefined)),
            modifiedTs: addButtonClicked ? (new Date()) : (updButtonClicked ? (new Date()) : (delButtonClicked ? (new Date()) : undefined)),
            deletedTs: addButtonClicked ? (undefined) : (updButtonClicked ? (undefined) : (delButtonClicked ? (new Date()) : undefined)),
        };
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDReportSecurityFormValues>) => {
        console.error('AUDReportSecurities validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Security Validation',
                'Please correct the highlighted Report Security fields and try again.',
                true
            )
        );
    };

    const onFinish = (values: AUDReportSecurityFormValues) => {
        const newManagedReportSecurity = buildManagedReportSecurityRequest(values);

        props.handleSetReportSecurities({
            operation: getRSOperation(),
            reportSecurity: newManagedReportSecurity,
        });

        close();
    };

    const setRSMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const clearRSSelection = () => {
        setRSSelectedReportSecurityID(-1);
        setRSSelectedReportSecurityTitle('');
        setRSSelectedReportSecurityCreatedBy(0);
        setRSSelectedReportSecurityCreatedTs(new Date());

        setRSUserID(0);
        setRSTenantID(0);
        setRSUserRoleID(null);

        setRSMode('add');
        handleRSReportSecurityResetForm('blank');
    };

    const handleRSSelectionChange = (value: number) => {
        const selectedReportSecurity = reportSecurities.find((reportSecurity) => reportSecurity.id === value);

        setRSSelectedReportSecurityID(value);
        setRSSelectedReportSecurityTitle(buildReportSecurityNameConstruct(selectedReportSecurity));

        setRSSelectedReportSecurityCreatedBy(selectedReportSecurity?.createdBy ?? 0);
        setRSSelectedReportSecurityCreatedTs(selectedReportSecurity?.createdTs ?? new Date());

        setRSMode('add');
        handleRSReportSecurityResetForm('blank');
    };

    const setRSBlankForm = () => {
        formAUDReportSecurity.setFieldValue('rsUserID', 0);
        formAUDReportSecurity.setFieldValue('rsTenantID', 0);
        formAUDReportSecurity.setFieldValue('rsUserRoleID', null);

        setRSUserID(0);
        setRSTenantID(0);
        setRSUserRoleID(null);
    };

    const setRSFormFromSelectedReportSecurity = () => {
        const selectedReportSecurity = reportSecurities?.find(
            (reportSecurity) => reportSecurity.id === rsSelectedReportSecurityID
        );

        formAUDReportSecurity.setFieldValue('rsUserID', selectedReportSecurity?.userId ?? 0);
        formAUDReportSecurity.setFieldValue('rsTenantID', selectedReportSecurity?.tenantId ?? 0);
        formAUDReportSecurity.setFieldValue('rsUserRoleID', selectedReportSecurity?.userRoleId ?? null);

        setRSUserID(selectedReportSecurity?.userId ?? 0);
        setRSTenantID(selectedReportSecurity?.tenantId ?? 0);
        setRSUserRoleID(selectedReportSecurity?.userRoleId ?? null);
    };

    const getRSOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getRSActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const handleRSReportSecurityResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setRSBlankForm();
            return;
        }

        setRSFormFromSelectedReportSecurity();
    };

    //=================================================================
    //=================================================================
    
    const handleRSAddReportSecurity = () => {
        clearRSSelection();
    };

    //=================================================================
    //=================================================================

    const handleRSUpdReportSecurity = () => {
        setRSMode('update');
        handleRSReportSecurityResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleRSDelReportSecurity = () => {
        setRSMode('delete');
        handleRSReportSecurityResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onRSCancel = () => {
        clearRSSelection();
    };

    const handleRSLoadError = (title: string, err: unknown) => {
        const apiError = err as ManagedApiError;

        const message =
            apiError?.response?.data?.message
                ? `${title}: ${apiError.response.data.message}`
                : `${title}: ${String(err)}`;

        console.error(title, err);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Security Load Error',
                message,
                true
            )
        );
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };
    
    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <Spin spinning={ isGetUsersLoading || isGetTenantsLoading }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Report Securities</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report: [' + formatRSDisplayID(rsPropsReportID) + '] ' + rsPropsReportGroupName + '-' + rsPropsReportName}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Security: [' + formatRSDisplayID(rsSelectedReportSecurityID) + '] ' + rsSelectedReportSecurityTitle}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select Report Security to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Report Security'}
                            showSearch
                            styles={{ popup: { root: { width: 500 } } }}
                            style={{paddingTop: '4px', width: '95%'}}
                            onChange={handleRSSelectionChange}
                        >
                            {reportSecurities.map(
                                (eachReportSecurity: ManagedReportSecurity) => {
                                    return (
                                        <Option
                                            key={eachReportSecurity.id}
                                            label={buildReportSecurityNameConstruct(eachReportSecurity)}
                                            value={eachReportSecurity.id}
                                        >
                                            {buildReportSecurityNameConstruct(eachReportSecurity)}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={5}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRSAddReportSecurity}
                        >
                            Add Report Security
                        </Button>
                    </Col>
                    <Col span={8} />
                    <Col span={5}>
                        <Button
                            disabled={rsSelectedReportSecurityID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRSUpdReportSecurity}
                        >
                            Update Report Security
                        </Button>
                    </Col>
                    <Col span={1} />
                    <Col span={5}>
                        <Button
                            disabled={rsSelectedReportSecurityID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRSDelReportSecurity}
                        >
                            Delete Report Security
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDReportSecurity}
                    initialValues={initialRSFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getRSActionColor() }}>
                                    {addButtonClicked ? 'Add Report Security' :
                                        (updButtonClicked ? 'Update Report Security: [' + formatRSDisplayID(rsSelectedReportSecurityID) + '] ' :
                                            (delButtonClicked ? 'Delete Report Security: [' + formatRSDisplayID(rsSelectedReportSecurityID) + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getRSActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleRSReportSecurityResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='rsUserID'
                                        rules={[
                                            {
                                                validator: validateRSDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='User ID:'
                                            optionFilterProp='label'
                                            showSearch
                                            value={rsUserID}
                                            onChange={handleRSUserIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {users.map(
                                                (eachUser) => {
                                                    return (
                                                        <Option
                                                            key={eachUser.id}
                                                            label={eachUser.firstName + ' ' + eachUser.lastName}
                                                            value={eachUser.id}
                                                        >
                                                            {eachUser.firstName + ' ' + eachUser.lastName}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='rsTenantID'
                                        rules={[
                                            {
                                                validator: validateRSDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='Tenants:'
                                            optionFilterProp='label'
                                            placeholder='Select Tenant'
                                            showSearch
                                            value={rsTenantID}
                                            onChange={handleRSTenantIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {tenants.map(
                                                (eachTenant) => {
                                                    return (
                                                        <Option
                                                            key={eachTenant.id}
                                                            label={eachTenant.name}
                                                            value={eachTenant.id}
                                                        >
                                                            {eachTenant.name}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={6}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        key='rsuserroleid'
                                        name='rsUserRoleID'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='User Role ID:'
                                            placeholder='Select User Role ID'
                                            showSearch
                                            value={rsUserRoleID}
                                            onChange={handleRSUserRoleIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            <Option
                                                value={1}
                                                label={'1'}
                                            >
                                                {'1 Admin'}
                                            </Option>
                                            <Option
                                                value={2}
                                                label={'2'}
                                            >
                                                {'2 Manager'}
                                            </Option>
                                            <Option
                                                value={3}
                                                label={'3'}
                                            >
                                                {'3 Nurse'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={18} />
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={5}>
                            <Form.Item
                                name='rsCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={9} />
                        <Col span={5}>
                            <Form.Item
                                name='rsApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add Report Security' : (updButtonClicked ? 'Update Report Security' : (delButtonClicked ? 'Delete Report Security' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name='rsCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onRSCancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDReportSecurities;
